import React, {useState, useEffect, createContext, useContext, useRef, useCallback} from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


// Define Firebase configuration from environment variables
// These are loaded from .env file in development and from hosting environment in production
const firebaseConfigFromEnv = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Set global variables with environment values or fallback to existing values if already defined
window.__firebase_config = window.__firebase_config || JSON.stringify(firebaseConfigFromEnv);
window.__initial_auth_token = window.__initial_auth_token || process.env.REACT_APP_INITIAL_AUTH_TOKEN || "";
window.__app_id = window.__app_id || process.env.REACT_APP_APP_ID || "financial-app-3ce13";

// Make these available globally
const __firebase_config = window.__firebase_config;
const __initial_auth_token = window.__initial_auth_token;
const __app_id = window.__app_id;



// Initialize Firebase with global variables provided by the environment.
// IMPORTANT: Use the __firebase_config provided by the Canvas environment
// This ensures compatibility with the __initial_auth_token.
const firebaseConfig = JSON.parse(typeof __firebase_config !== 'undefined' ? __firebase_config : '{}');

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Context for managing global state like user and database instances
const AppContext = createContext(null);

// Main App component
export default function App() {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('login'); // 'login', 'dashboard', 'transactions', 'goals', 'reminders', 'history'
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExplicitSignOut, setIsExplicitSignOut] = useState(false); // New state to track explicit sign-out
  const [isPartner, setIsPartner] = useState(false); // New state to track if the current user is the partner (girlfriend)

  // Firebase Authentication Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (currentUser) {
          // If a user is logged in (either explicitly or anonymously by initial process)
          setUser(currentUser);
          setUserId(currentUser.uid);
          setCurrentScreen('dashboard');
          setError(''); // Clear any previous error
          setIsExplicitSignOut(false); // Reset this flag if a user is logged in

          // Reset partner status by default
          setIsPartner(false);

          // Check if this user is a partner in any shared cards
          try {
            const allCardsQuery = query(collection(db, `artifacts/${__app_id}/users`));
            const allUsersSnapshot = await getDocs(allCardsQuery);

            let foundAsPartner = false;

            // Check each user's cards
            for (const userDoc of allUsersSnapshot.docs) {
              const cardsQuery = collection(db, `artifacts/${__app_id}/users/${userDoc.id}/cards`);
              const cardsSnapshot = await getDocs(cardsQuery);

              // Check each card
              for (const cardDoc of cardsSnapshot.docs) {
                const cardData = cardDoc.data();

                // If this card is shared with the current user
                if (cardData.isShared && cardData.sharedWithUserId === currentUser.uid) {
                  foundAsPartner = true;
                  break;
                }
              }

              // If we already found that this user is a partner, no need to check more users
              if (foundAsPartner) break;
            }

            // Update the state based on our findings
            setIsPartner(foundAsPartner);
          } catch (error) {
            console.error("Error checking if user is a partner:", error);
          }
        } else {
          // If no user is currently authenticated (currentUser is null)
          setUser(null);
          setUserId(null);

          if (isExplicitSignOut) {
            // If the signOut was explicitly initiated by the user, go to login screen
            setCurrentScreen('login');
            setError(''); // Clear error on successful transition
            setIsExplicitSignOut(false); // Reset the flag
          } else {
            // If no user, and it's NOT an explicit sign-out (e.g., initial load or session expired),
            // attempt automatic sign-in with custom token or anonymously.
            try {
              if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
                await signInWithCustomToken(auth, __initial_auth_token);
              } else {
                // Skip anonymous authentication as it's likely disabled in Firebase settings
                // Just go to login screen instead
                setCurrentScreen('login');
                // No error needed here as we're intentionally skipping anonymous auth
                 // Exit early to avoid setting error
              }
            } catch (e) {
              console.error("Automatic login attempt failed:", e);
              setError(`Automatic login failed: ${e.message}. Please try logging in manually.`);
              setCurrentScreen('login'); // Force login screen on automatic sign-in failure
            }
          }
        }
      } catch (outerError) {
        console.error("Unhandled error in onAuthStateChanged:", outerError);
        setError(`An unexpected error occurred during authentication: ${outerError.message}`);
        setCurrentScreen('login'); // Fallback to login screen
        setUser(null);
        setUserId(null);
      } finally {
        setIsAuthReady(true);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [isExplicitSignOut]); // Add isExplicitSignOut to dependencies

  if (loading || !isAuthReady) {
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="text-xl font-semibold text-gray-700">Loading app...</div>
        </div>
    );
  }

  const contextValue = {
    user,
    userId,
    db,
    auth,
    currentScreen,
    setCurrentScreen,
    selectedCardId,
    setSelectedCardId,
    setError,
    setIsExplicitSignOut, // Provide this setter to Header for sign-out
    isPartner,
    setIsPartner,
  };

  return (
      <AppContext.Provider value={contextValue}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-inter antialiased">
          <Header />
          <div className="container mx-auto p-4 max-w-5xl">
            {error && <ErrorMessage message={error} />}
            {currentScreen === 'login' && <AuthScreen />}
            {currentScreen === 'dashboard' && <DashboardScreen />}
            {currentScreen === 'transactions' && <TransactionsScreen />}
            {currentScreen === 'goals' && <GoalsScreen />}
            {currentScreen === 'reminders' && <RemindersScreen />}
            {currentScreen === 'history' && <HistoryScreen />}
          </div>
        </div>
      </AppContext.Provider>
  );
}

// Header Component
function Header() {
  const { user, auth, setCurrentScreen, setError, setIsExplicitSignOut } = useContext(AppContext); // Destructure setIsExplicitSignOut
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsMenuOpen, setIsProfileMenuOpen]);

  const handleSignOut = async () => {
    setIsExplicitSignOut(true); // Signal that an explicit sign-out is initiated
    try {
      await signOut(auth);
      setError('');
    } catch (e) {
      console.error('Error signing out:', e);
      setError(`Sign out failed: ${e.message}.`);
      // Even if signOut fails, the isExplicitSignOut flag will ensure we try to go to login.
    } finally {
      setIsProfileMenuOpen(false); // Close profile menu
      // The screen change is now handled by the onAuthStateChanged listener in App component
      // based on the isExplicitSignOut flag.
    }
  };

  const navigateTo = (screen) => {
    setCurrentScreen(screen);
    setIsMenuOpen(false);
    setIsProfileMenuOpen(false);
  };

  const getDisplayName = () => {
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return user?.uid ? `User-${user.uid.substring(0, 4)}` : 'Guest';
  };

  const getProfileInitial = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
      <header className="bg-white shadow-lg p-4 rounded-b-lg flex justify-between items-center mb-6 relative z-10 border-t-4 border-indigo-600">
        {user && (
            <div className="relative" ref={menuRef}>
              <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300 ease-in-out"
                  aria-label="Open navigation menu"
              >
                <svg
                    className="w-6 h-6 text-indigo-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </button>
              {isMenuOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                    <button
                        onClick={() => navigateTo('dashboard')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Dashboard
                    </button>
                    <button
                        onClick={() => navigateTo('goals')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Goals
                    </button>
                    <button
                        onClick={() => navigateTo('reminders')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Reminders
                    </button>
                    <button
                        onClick={() => navigateTo('history')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      History
                    </button>
                  </div>
              )}
            </div>
        )}

        <h1 className="text-2xl font-bold text-indigo-700 mx-auto flex items-center">
          <svg className="w-8 h-8 mr-2 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path>
            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"></path>
          </svg>
          Pocket Pal
        </h1>

        {user && (
            <div className="relative" ref={profileMenuRef}>
              <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300 ease-in-out"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                  {getProfileInitial()}
                </div>
                <span className="text-indigo-700 font-semibold hidden md:block">{getDisplayName()}</span>
              </button>
              {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                    <div className="block px-4 py-2 text-sm text-indigo-700 font-semibold border-b border-gray-200">
                      {user.email || getDisplayName()}
                    </div>
                    <button
                        onClick={() => { alert('Settings functionality not yet implemented.'); setIsProfileMenuOpen(false); }}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Settings
                    </button>

                    <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Sign Out
                    </button>
                  </div>
              )}
            </div>
        )}
      </header>
  );
}

// Error Message Component
function ErrorMessage({ message }) {
  if (!message) return null;
  return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-md relative mb-4" role="alert">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <strong className="font-bold">Error! </strong>
          <span className="ml-1">{message}</span>
        </div>
      </div>
  );
}

// Auth Screen Component
function AuthScreen() {
  const { auth, setCurrentScreen, setError } = useContext(AppContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setCurrentScreen('dashboard');
    } catch (e) {
      console.error('Login error:', e);
      if (e.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (e.code === 'auth/user-disabled') {
        setError('This user account has been disabled.');
      } else if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password') {
        setError('Incorrect email or password.');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    }
  };

  return (
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md mx-auto mt-10 border border-indigo-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path>
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"></path>
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-indigo-700 mb-2">Welcome Back!</h2>
          <p className="text-gray-600">Sign in to manage your finances.</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-gray-700 text-sm font-semibold mb-2">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <input
                  type="email"
                  id="email"
                  className="w-full pl-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300 ease-in-out"
                  placeholder="your@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
              />
            </div>
          </div>
          <div>
            <label htmlFor="password" className="block text-gray-700 text-sm font-semibold mb-2">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                  type="password"
                  id="password"
                  className="w-full pl-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-300 ease-in-out"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
              />
            </div>
          </div>
          <button
              type="submit"
              className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg transform hover:scale-105"
          >
            Sign In
          </button>
        </form>
        <p className="text-sm text-gray-500 text-center mt-6">
          Welcome to your personal finance management app.
        </p>
      </div>
  );
}

// Dashboard Screen Component
function DashboardScreen() {
  const { userId, db, setCurrentScreen, setSelectedCardId, setError } = useContext(AppContext);
  const [cards, setCards] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [newCardType, setNewCardType] = useState('debit');
  const [newCreditLimit, setNewCreditLimit] = useState('');
  const [newBudgetLimit, setNewBudgetLimit] = useState('');
  const [newCutDate, setNewCutDate] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newIsShared, setNewIsShared] = useState(false);
  const [newSharedWithUserId, setNewSharedWithUserId] = useState('');
  const [newSplitPercentage, setNewSplitPercentage] = useState('70');
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);
  const [financialAnalysis, setFinancialAnalysis] = useState('');
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => {
    if (!userId || !db) return;
    const cardsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/cards`);
    const unsubscribe = onSnapshot(cardsCollectionRef, (snapshot) => {
      const fetchedCards = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCards(fetchedCards);
      setError('');
    }, (error) => {
      console.error("Error fetching cards:", error);
      setError("Failed to load cards. Please refresh the page.");
    });
    return () => unsubscribe();
  }, [userId, db, setError]);

  useEffect(() => {
    if (!userId || !db) return;
    const transactionsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/transactions`);
    const unsubscribe = onSnapshot(transactionsCollectionRef, (snapshot) => {
      const fetchedTransactions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date ? new Date(doc.data().date) : new Date(doc.data().createdAt?.toDate() || new Date()),
      }));
      setAllTransactions(fetchedTransactions);
    }, (error) => {
      console.error("Error fetching all transactions:", error);
      setError("Failed to load all transactions for analysis.");
    });
    return () => unsubscribe();
  }, [userId, db, setError]);

  useEffect(() => {
    if (!userId || !db) return;
    const remindersCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/reminders`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
        remindersCollectionRef,
        where('isCompleted', '==', false),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedReminders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().dueDate,
      }));

      const filteredReminders = fetchedReminders
          .filter(r => new Date(r.dueDate) >= today)
          .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

      setUpcomingReminders(filteredReminders);
    }, (error) => {
      console.error("Error fetching reminders:", error);
      setError("Failed to load upcoming reminders.");
    });

    return () => unsubscribe();
  }, [userId, db, setError]);

  useEffect(() => {
    if (!userId || !db) return;
    const goalsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/goals`);
    const unsubscribe = onSnapshot(goalsCollectionRef, (snapshot) => {
      const fetchedGoals = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGoals(fetchedGoals);
      setError('');
    }, (error) => {
      console.error("Error fetching goals:", error);
      setError("Failed to load goals for summary.");
    });
    return () => unsubscribe();
  }, [userId, db, setError]);

  const totalBalance = cards.reduce((acc, card) => acc + (card.currentBalance || 0), 0);
  const totalCreditLimit = cards.filter(card => card.type === 'credit').reduce((acc, card) => acc + (card.creditLimit || 0), 0);

  const today = new Date();
  const currentMonthTransactions = allTransactions.filter(transaction => {
    return transaction.date.getMonth() === today.getMonth() && transaction.date.getFullYear() === today.getFullYear();
  });

  const currentMonthExpenses = currentMonthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const currentMonthIncome = currentMonthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

  const totalBudgetLimit = cards.reduce((acc, card) => acc + (card.budgetLimit || 0), 0);
  const isOnBudgetThisMonth = totalBudgetLimit > 0 ? (currentMonthExpenses <= totalBudgetLimit) : false;

  let closestCutDateCard = null;
  let smallestDaysUntilCut = Infinity;
  cards.filter(card => card.type === 'credit' && card.cutDate).forEach(card => {
    const cutDate = new Date(card.cutDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilCut = Math.ceil((cutDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilCut >= 0 && daysUntilCut < smallestDaysUntilCut) {
      smallestDaysUntilCut = daysUntilCut;
      closestCutDateCard = card;
    }
  });

  let closestDueDateCard = null;
  let smallestDaysUntilDue = Infinity;
  cards.filter(card => card.type === 'credit' && card.dueDate).forEach(card => {
    const dueDate = new Date(card.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilDue >= 0 && daysUntilDue < smallestDaysUntilDue) {
      smallestDaysUntilDue = daysUntilDue;
      closestDueDateCard = card;
    }
  });

  const handleAddCard = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError('User not authenticated.');
      return;
    }

    try {
      if (isEditingCard) {
        // Update existing card
        const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, editingCardId);

        // Update card data
        await updateDoc(cardDocRef, {
          name: newCardName,
          type: newCardType,
          creditLimit: newCardType === 'credit' ? parseFloat(newCreditLimit || 0) : null,
          budgetLimit: parseFloat(newBudgetLimit || 0),
          cutDate: newCardType === 'credit' ? newCutDate : null,
          dueDate: newCardType === 'credit' ? newDueDate : null,
          isShared: newIsShared,
          sharedWithUserId: newIsShared ? newSharedWithUserId : null,
          splitPercentage: newIsShared ? parseFloat(newSplitPercentage || 70) : null,
        });

        // Reset editing state
        setIsEditingCard(false);
        setEditingCardId(null);
      } else {
        // Add new card
        const cardsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/cards`);
        const docRef = await addDoc(cardsCollectionRef, {
          name: newCardName,
          type: newCardType,
          creditLimit: newCardType === 'credit' ? parseFloat(newCreditLimit || 0) : null,
          budgetLimit: parseFloat(newBudgetLimit || 0),
          cutDate: newCardType === 'credit' ? newCutDate : null,
          dueDate: newCardType === 'credit' ? newDueDate : null,
          currentBalance: 0,
          createdAt: new Date(),
          isShared: newIsShared,
          sharedWithUserId: newIsShared ? newSharedWithUserId : null,
          splitPercentage: newIsShared ? parseFloat(newSplitPercentage || 70) : null,
        });

        const remindersCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/reminders`);
        if (newCardType === 'credit' && newDueDate) {
          await addDoc(remindersCollectionRef, {
            title: `Payment Due for ${newCardName}`,
            description: `Credit card payment for ${newCardName} is due.`,
            dueDate: newDueDate,
            isCompleted: false,
            type: 'card_payment',
            relatedCardId: docRef.id,
            createdAt: new Date(),
          });
        }
        if (newCardType === 'credit' && newCutDate) {
          await addDoc(remindersCollectionRef, {
            title: `Cut-off Date for ${newCardName}`,
            description: `Credit card statement cut-off date for ${newCardName}.`,
            dueDate: newCutDate,
            isCompleted: false,
            type: 'card_cut_date',
            relatedCardId: docRef.id,
            createdAt: new Date(),
          });
        }

        // Add a reminder 5 days before the due date for credit cards
        if (newCardType === 'credit' && newDueDate) {
          // Calculate date 5 days before due date
          const dueDateObj = new Date(newDueDate);
          dueDateObj.setDate(dueDateObj.getDate() - 5);
          const paymentReminderDate = dueDateObj.toISOString().split('T')[0]; // Format as YYYY-MM-DD

          await addDoc(remindersCollectionRef, {
            title: `Payment Reminder for ${newCardName}`,
            description: `Your payment for ${newCardName} is due in 5 days. Please make your payment to avoid late fees.`,
            dueDate: paymentReminderDate,
            isCompleted: false,
            type: 'card_payment_reminder',
            relatedCardId: docRef.id,
            createdAt: new Date(),
          });
        }
      }

      // Reset form state
      setIsAddingCard(false);
      setNewCardName('');
      setNewCardType('debit');
      setNewCreditLimit('');
      setNewBudgetLimit('');
      setNewCutDate('');
      setNewDueDate('');
      setNewIsShared(false);
      setNewSharedWithUserId('');
      setNewSplitPercentage('70');
      setError('');
    } catch (e) {
      console.error(`Error ${isEditingCard ? 'updating' : 'adding'} card:`, e);
      setError(`Failed to ${isEditingCard ? 'update' : 'add'} card. Please check your input.`);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }
    const confirmed = window.confirm("Are you sure you want to delete this card and all its transactions?");
    if (!confirmed) return;

    try {
      const transactionsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/transactions`);
      const qTransactions = query(transactionsCollectionRef, where('cardId', '==', cardId));
      const querySnapshotTransactions = await getDocs(qTransactions);
      const deleteTransactionPromises = [];
      querySnapshotTransactions.forEach((doc) => {
        deleteTransactionPromises.push(deleteDoc(doc.ref));
      });
      await Promise.all(deleteTransactionPromises);

      const remindersCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/reminders`);
      const qReminders = query(remindersCollectionRef, where('relatedCardId', '==', cardId));
      const querySnapshotReminders = await getDocs(qReminders);
      const deleteReminderPromises = [];
      querySnapshotReminders.forEach((doc) => {
        deleteReminderPromises.push(deleteDoc(doc.ref));
      });
      await Promise.all(deleteReminderPromises);

      const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, cardId);
      await deleteDoc(cardDocRef);
      setError('');
    } catch (e) {
      console.error("Error deleting card:", e);
      setError("Failed to delete card. Please try again.");
    }
  };

  const handleEditCard = (card) => {
    // Set form to editing mode
    setIsEditingCard(true);
    setEditingCardId(card.id);
    setIsAddingCard(true);

    // Populate form with card data
    setNewCardName(card.name);
    setNewCardType(card.type);
    setNewCreditLimit(card.creditLimit ? card.creditLimit.toString() : '');
    setNewBudgetLimit(card.budgetLimit ? card.budgetLimit.toString() : '');
    setNewCutDate(card.cutDate || '');
    setNewDueDate(card.dueDate || '');
    setNewIsShared(card.isShared || false);
    setNewSharedWithUserId(card.sharedWithUserId || '');
    setNewSplitPercentage(card.splitPercentage ? card.splitPercentage.toString() : '70');

    // Scroll to the form
    const cardFormElement = document.getElementById('cardForm');
    if (cardFormElement) {
      cardFormElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCardSelect = (cardId) => {
    setSelectedCardId(cardId);
    setCurrentScreen('transactions');
  };

  const getFinancialHealthAnalysis = async () => {
    setAnalysisLoading(true);
    setError('');
    setFinancialAnalysis('');

    const financialData = {
      totalBalance: totalBalance.toFixed(2),
      totalCreditLimit: totalCreditLimit.toFixed(2),
      currentMonthExpenses: currentMonthExpenses.toFixed(2),
      currentMonthIncome: currentMonthIncome.toFixed(2),
      isOnBudgetThisMonth: isOnBudgetThisMonth,
      closestCutDateCard: closestCutDateCard ? `${closestCutDateCard.name} (cut in ${smallestDaysUntilCut} days)` : 'N/A',
      closestDueDateCard: closestDueDateCard ? `${closestDueDateCard.name} (due in ${smallestDaysUntilDue} days)` : 'N/A',
      numCards: cards.length,
      numTransactionsThisMonth: currentMonthTransactions.length,
    };

    const prompt = `Provide a concise financial health analysis based on the following data for the current user:
    - Total Net Balance: $${financialData.totalBalance}
    - Total Credit Limit (across credit cards): $${financialData.totalCreditLimit}
    - Current Month's Expenses: $${financialData.currentMonthExpenses}
    - Current Month's Income: $${financialData.currentMonthIncome}
    - Is on Budget this month: ${financialData.isOnBudgetThisMonth ? 'Yes' : 'No'}
    - Credit card with closest 'fecha de corte': ${financialData.closestCutDateCard}
    - Credit card with closest payment due date: ${financialData.closestDueDateCard}
    - Number of cards: ${financialData.numCards}
    - Number of transactions this month: ${financialData.numTransactionsThisMonth}

    Consider the user's financial habits and highlight strengths, areas for improvement, and general advice. Keep the analysis to about 3-5 sentences.`;

    try {
      let chatHistory = [];
      chatHistory.push({ role: "user", parts: [{ text: prompt }] });
      const payload = { contents: chatHistory };
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.candidates && result.candidates.length > 0 &&
          result.candidates[0].content && result.candidates[0].content.parts &&
          result.candidates[0].content.parts.length > 0) {
        setFinancialAnalysis(result.candidates[0].content.parts[0].text);
      } else {
        setError('Failed to get analysis. Please try again.');
      }
    } catch (e) {
      console.error("Error fetching financial analysis:", e);
      setError('Failed to generate analysis due to a network or API error.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
      <div className="bg-white p-8 rounded-lg shadow-xl">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6">Your Financial Dashboard</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-indigo-500 text-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center transform hover:scale-105 transition-transform duration-300">
            <p className="text-sm font-semibold">Total Net Balance</p>
            <p className="text-4xl font-extrabold mt-2">
              ${totalBalance.toFixed(2)}
            </p>
          </div>
          <div className="bg-purple-500 text-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center transform hover:scale-105 transition-transform duration-300">
            <p className="text-sm font-semibold">Total Credit Limit</p>
            <p className="text-4xl font-extrabold mt-2">
              ${totalCreditLimit.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Financial Health Summary */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h3 className="text-2xl font-semibold text-gray-800 mb-4">Financial Health Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="text-sm font-medium text-blue-700">Current Month Expenses</p>
              <p className="text-xl font-bold text-blue-900">${currentMonthExpenses.toFixed(2)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-md">
              <p className="text-sm font-medium text-green-700">Current Month Income</p>
              <p className="text-xl font-bold text-green-900">${currentMonthIncome.toFixed(2)}</p>
            </div>
          </div>

          {/* Income vs Expense Bar Chart */}
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="text-lg font-semibold text-gray-700 mb-2">Income vs. Expense (Current Month)</h4>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-green-600">Income</span>
              <span className="text-red-600">Expenses</span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full flex overflow-hidden">
              <div
                  className="bg-green-500 h-full"
                  style={{ width: `${(currentMonthIncome + currentMonthExpenses) > 0 ? (currentMonthIncome / (currentMonthIncome + currentMonthExpenses)) * 100 : 0}%` }}
                  title={`Income: $${currentMonthIncome.toFixed(2)}`}
              ></div>
              <div
                  className="bg-red-500 h-full"
                  style={{ width: `${(currentMonthIncome + currentMonthExpenses) > 0 ? (currentMonthExpenses / (currentMonthIncome + currentMonthExpenses)) * 100 : 0}%` }}
                  title={`Expenses: $${currentMonthExpenses.toFixed(2)}`}
              ></div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between bg-indigo-50 p-4 rounded-md">
            <p className="text-lg font-semibold text-indigo-800">
              Current Month On Budget:
              <span className={`ml-2 font-bold ${isOnBudgetThisMonth ? 'text-green-600' : 'text-red-600'}`}>
              {isOnBudgetThisMonth ? 'YES!' : 'NO'}
            </span>
            </p>
          </div>
          <p className="text-gray-600 text-sm mt-2">
            Note: "On Budget" is calculated based on total expenses vs. total budget limit across all cards this month.
          </p>

          <div className="mt-6 border-t pt-4 border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Quick Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Display Closest Cut Date Card */}
              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm font-medium text-blue-700">Closest Cut Date</p>
                {closestCutDateCard && smallestDaysUntilCut !== Infinity ? (
                    <p className="text-lg font-bold text-blue-900">
                      {closestCutDateCard.name} (Cut in {smallestDaysUntilCut} days)
                    </p>
                ) : (
                    <p className="text-lg text-gray-600">No credit cards with cut dates.</p>
                )}
              </div>
              {/* Display Closest Payment Due Date */}
              <div className="bg-yellow-50 p-4 rounded-md">
                <p className="text-sm font-medium text-yellow-700">Closest Payment Due Date</p>
                {closestDueDateCard && smallestDaysUntilDue !== Infinity ? (
                    <p className="text-lg font-bold text-yellow-900">
                      {closestDueDateCard.name} (Due in {smallestDaysUntilDue} days)
                    </p>
                ) : (
                    <p className="text-lg text-gray-600">No credit cards with due dates.</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">AI Financial Health Analysis</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Get a quick analysis of your overall financial health based on your current data. This analysis is generated
              by the Gemini model and is specific to your logged-in account.
            </p>
            <button
                onClick={getFinancialHealthAnalysis}
                disabled={analysisLoading}
                className={`w-full py-3 rounded-md font-semibold text-white transition duration-300 ease-in-out shadow-lg ${
                    analysisLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transform hover:scale-105'
                }`}
            >
              {analysisLoading ? 'Generating Analysis...' : 'Get My Financial Health Analysis'}
            </button>
            {financialAnalysis && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-900">
                  <p className="font-semibold mb-2">Your Analysis:</p>
                  <p className="text-sm leading-relaxed">{financialAnalysis}</p>
                </div>
            )}
          </div>

          {/* Upcoming Reminders Section */}
          <div className="mt-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Upcoming Reminders</h3>
            {upcomingReminders.length === 0 ? (
                <p className="text-gray-600">No upcoming reminders. Add some or manage cards!</p>
            ) : (
                <div className="space-y-3">
                  {upcomingReminders.slice(0, 5).map((reminder) => ( // Show top 5 upcoming reminders
                      <div key={reminder.id} className="bg-yellow-50 p-4 rounded-md shadow-sm border border-yellow-200">
                        <p className="text-lg font-semibold text-yellow-800">{reminder.title}</p>
                        <p className="text-sm text-gray-700">{reminder.description}</p>
                        <p className="text-sm font-medium text-yellow-700 mt-1">Due: {new Date(reminder.dueDate).toLocaleDateString()}</p>
                      </div>
                  ))}
                  {upcomingReminders.length > 5 && (
                      <p className="text-sm text-gray-600 mt-2">
                        <button onClick={() => setCurrentScreen('reminders')} className="text-indigo-600 hover:underline">
                          View all {upcomingReminders.length} reminders
                        </button>
                      </p>
                  )}
                </div>
            )}
          </div>

          {/* Goals Summary Section */}
          <div className="mt-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Your Goals Summary</h3>
            {goals.length === 0 ? (
                <p className="text-gray-600">No goals added yet. Set your financial aspirations!</p>
            ) : (
                <div className="space-y-3">
                  {goals.slice(0, 3).map((goal) => { // Show top 3 goals
                    const progress = goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0;
                    const isCompleted = goal.currentAmount >= goal.targetAmount;
                    return (
                        <div key={goal.id} className="bg-blue-50 p-4 rounded-md shadow-sm border border-blue-200">
                          <p className={`text-lg font-semibold ${isCompleted ? 'text-green-700' : 'text-blue-800'}`}>
                            {goal.name} {isCompleted && <span className="text-sm text-green-500">(Completed!)</span>}
                          </p>
                          <p className="text-sm text-gray-700">Progress: ${goal.currentAmount.toFixed(2)} / ${goal.targetAmount.toFixed(2)}</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                                className={`h-2 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{progress.toFixed(1)}% complete</p>
                        </div>
                    );
                  })}
                  {goals.length > 3 && (
                      <p className="text-sm text-gray-600 mt-2">
                        <button onClick={() => setCurrentScreen('goals')} className="text-indigo-600 hover:underline">
                          View all {goals.length} goals
                        </button>
                      </p>
                  )}
                </div>
            )}
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4">Your Cards</h3>
        {cards.length === 0 ? (
            <p className="text-gray-600 mb-6">No cards added yet. Add your first card to get started!</p>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {cards.map((card) => (
                  <div
                      key={card.id}
                      className={`bg-white p-5 rounded-lg shadow-md border ${card.isShared ? 'border-pink-300' : 'border-gray-200'} transform hover:scale-105 transition-transform duration-300 cursor-pointer flex flex-col justify-between`}
                      onClick={() => handleCardSelect(card.id)}
                  >
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-xl font-bold text-indigo-600">{card.name}</h4>
                        {card.isShared && (
                          <span className="bg-pink-100 text-pink-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                            Shared
                          </span>
                        )}
                      </div>
                      <p className={`text-sm font-semibold mb-2 ${card.type === 'credit' ? 'text-purple-600' : 'text-blue-600'}`}>
                        {card.type.toUpperCase()} Card
                      </p>
                      <p className="text-gray-700 text-sm">Balance: <span className="font-semibold">${(card.currentBalance || 0).toFixed(2)}</span></p>
                      {card.type === 'credit' && (
                          <>
                            <p className="text-gray-700 text-sm">Credit Limit: <span className="font-semibold">${(card.creditLimit || 0).toFixed(2)}</span></p>
                            <p className="text-gray-700 text-sm">Cut Date: <span className="font-semibold">{card.cutDate || 'N/A'}</span></p>
                            <p className="text-gray-700 text-sm">Payment Due: <span className="font-semibold">{card.dueDate || 'N/A'}</span></p>
                          </>
                      )}
                      {card.budgetLimit !== null && card.budgetLimit !== undefined && (
                          <p className="text-gray-700 text-sm">Budget Limit: <span className="font-semibold">${(card.budgetLimit || 0).toFixed(2)}</span></p>
                      )}
                      {card.isShared && (
                          <div className="mt-2 p-2 bg-pink-50 rounded-md">
                            <p className="text-xs text-pink-800 font-medium">Shared with another user</p>
                            <p className="text-xs text-gray-600">Your split: <span className="font-semibold">{card.splitPercentage}%</span></p>
                            <p className="text-xs text-gray-600">Their split: <span className="font-semibold">{100 - (card.splitPercentage || 0)}%</span></p>
                          </div>
                      )}
                    </div>
                    <div className="mt-4 self-end flex space-x-2">
                      <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card selection when editing
                            handleEditCard(card);
                          }}
                          className="px-3 py-1 bg-blue-400 text-white rounded-md text-xs hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 transition duration-300 ease-in-out"
                      >
                        Edit Card
                      </button>
                      <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card selection when deleting
                            handleDeleteCard(card.id);
                          }}
                          className="px-3 py-1 bg-red-400 text-white rounded-md text-xs hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition duration-300 ease-in-out"
                      >
                        Delete Card
                      </button>
                    </div>
                  </div>
              ))}
            </div>
        )}

        <button
            onClick={() => setIsAddingCard(!isAddingCard)}
            className="px-6 py-3 bg-green-500 text-white font-semibold rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg transform hover:scale-105"
        >
          {isAddingCard ? 'Cancel Add Card' : 'Add New Card'}
        </button>

        {isAddingCard && (
            <div id="cardForm" className="mt-8 p-6 bg-gray-50 rounded-lg shadow-inner">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                {isEditingCard ? 'Edit Card' : 'Add New Card'}
              </h3>
              <form onSubmit={handleAddCard} className="space-y-4">
                <div>
                  <label htmlFor="cardName" className="block text-gray-700 text-sm font-semibold mb-2">
                    Card Name
                  </label>
                  <input
                      type="text"
                      id="cardName"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newCardName}
                      onChange={(e) => setNewCardName(e.target.value)}
                      placeholder="e.g., My Visa, Joint Debit"
                      required
                  />
                </div>
                <div>
                  <label htmlFor="cardType" className="block text-gray-700 text-sm font-semibold mb-2">
                    Card Type
                  </label>
                  <select
                      id="cardType"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newCardType}
                      onChange={(e) => setNewCardType(e.target.value)}
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                {newCardType === 'credit' && (
                    <>
                      <div>
                        <label htmlFor="creditLimit" className="block text-gray-700 text-sm font-semibold mb-2">
                          Credit Limit ($)
                        </label>
                        <input
                            type="number"
                            id="creditLimit"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={newCreditLimit}
                            onChange={(e) => setNewCreditLimit(e.target.value)}
                            placeholder="e.g., 5000"
                            min="0"
                        />
                      </div>
                      <div>
                        <label htmlFor="cutDate" className="block text-gray-700 text-sm font-semibold mb-2">
                          Cut-off Date (YYYY-MM-DD)
                        </label>
                        <input
                            type="date"
                            id="cutDate"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={newCutDate}
                            onChange={(e) => setNewCutDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label htmlFor="dueDate" className="block text-gray-700 text-sm font-semibold mb-2">
                          Payment Due Date (YYYY-MM-DD)
                        </label>
                        <input
                            type="date"
                            id="dueDate"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={newDueDate}
                            onChange={(e) => setNewDueDate(e.target.value)}
                        />
                      </div>
                    </>
                )}
                <div>
                  <label htmlFor="budgetLimit" className="block text-gray-700 text-sm font-semibold mb-2">
                    Monthly Budget Limit ($)
                  </label>
                  <input
                      type="number"
                      id="budgetLimit"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newBudgetLimit}
                      onChange={(e) => setNewBudgetLimit(e.target.value)}
                      placeholder="e.g., 1000"
                      min="0"
                  />
                </div>

                {/* Card Sharing Options */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Card Sharing Options</h4>

                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="isShared"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={newIsShared}
                      onChange={(e) => setNewIsShared(e.target.checked)}
                    />
                    <label htmlFor="isShared" className="ml-2 block text-gray-700 text-sm font-semibold">
                      Share this card with someone
                    </label>
                  </div>

                  {newIsShared && (
                    <>
                      <div className="mb-4">
                        <label htmlFor="sharedWithUserId" className="block text-gray-700 text-sm font-semibold mb-2">
                          User ID to share with
                        </label>
                        <input
                          type="text"
                          id="sharedWithUserId"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={newSharedWithUserId}
                          onChange={(e) => setNewSharedWithUserId(e.target.value)}
                          placeholder="Enter the user ID of your girlfriend"
                          required={newIsShared}
                        />
                        <p className="text-xs text-gray-500 mt-1">This is the Firebase user ID of the person you want to share with</p>
                      </div>

                      <div>
                        <label htmlFor="splitPercentage" className="block text-gray-700 text-sm font-semibold mb-2">
                          Your Split Percentage (%)
                        </label>
                        <div className="flex items-center">
                          <input
                            type="range"
                            id="splitPercentage"
                            className="w-full mr-4"
                            min="0"
                            max="100"
                            step="5"
                            value={newSplitPercentage}
                            onChange={(e) => setNewSplitPercentage(e.target.value)}
                          />
                          <input
                            type="number"
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={newSplitPercentage}
                            onChange={(e) => setNewSplitPercentage(e.target.value)}
                            min="0"
                            max="100"
                          />
                          <span className="ml-1">%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This is the percentage of shared expenses you will pay. The other person will pay {100 - parseInt(newSplitPercentage, 10)}%.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex space-x-4">
                  {isEditingCard && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingCard(false);
                        setEditingCardId(null);
                        setNewCardName('');
                        setNewCardType('debit');
                        setNewCreditLimit('');
                        setNewBudgetLimit('');
                        setNewCutDate('');
                        setNewDueDate('');
                        setNewIsShared(false);
                        setNewSharedWithUserId('');
                        setNewSplitPercentage('70');
                      }}
                      className="w-full py-2 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-sm"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="w-full py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-sm"
                  >
                    {isEditingCard ? 'Update Card' : 'Create Card'}
                  </button>
                </div>
              </form>
            </div>
        )}
      </div>
  );
}

// Transactions Screen Component
function TransactionsScreen() {
  const { userId, db, setCurrentScreen, selectedCardId, setSelectedCardId, setError, isPartner } = useContext(AppContext);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [newTransactionAmount, setNewTransactionAmount] = useState('');
  const [newTransactionDescription, setNewTransactionDescription] = useState('');
  const [newTransactionType, setNewTransactionType] = useState('expense'); // 'expense' or 'income'
  const [newTransactionCategory, setNewTransactionCategory] = useState('');
  const [newTransactionDate, setNewTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [newTransactionHighlighted, setNewTransactionHighlighted] = useState(false);
  const [isInstallmentPlanStart, setIsInstallmentPlanStart] = useState(false); // New: for installment
  const [installmentTotalAmount, setInstallmentTotalAmount] = useState(''); // New: for installment total amount
  const [selectedPeriod, setSelectedPeriod] = useState('current'); // 'current', 'previous', 'previous2'
  const [allTransactionsData, setAllTransactionsData] = useState([]);
  const [newTransactionOwner, setNewTransactionOwner] = useState('me'); // 'me', 'other', 'both'
    const [customSplitPercentage, setCustomSplitPercentage] = useState(70); // Default to 70% for the main user
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);

  // Fetch selected card details
  useEffect(() => {
    if (!userId || !db || !selectedCardId) return;

    const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, selectedCardId);
    const unsubscribe = onSnapshot(cardDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setSelectedCard({ id: docSnap.id, ...docSnap.data() });
        setError('');
      } else {
        setSelectedCard(null);
        setError("Card not found.");
      }
    }, (error) => {
      console.error("Error fetching selected card:", error);
      setError("Failed to load card details.");
    });

    return () => unsubscribe();
  }, [userId, db, selectedCardId, setError]);

  const filterTransactionsByPeriod = useCallback((allTransactions, period) => {
    if (!selectedCard || !selectedCard.cutDate) {
      // If no cut date, fall back to calendar month filtering
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      const filteredTransactions = allTransactions.filter(transaction => {
        return transaction.date.getMonth() === currentMonth && transaction.date.getFullYear() === currentYear;
      });

      setTransactions(filteredTransactions);
      return;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    // Parse the cut date (e.g., "3" for the 3rd of each month)
    const cutDay = parseInt(selectedCard.cutDate.split('-')[2], 10);

    // Calculate the current period's start date (most recent past cut date)
    let currentPeriodStart = new Date(today);
    currentPeriodStart.setDate(cutDay);

    // If today is before this month's cut date, use previous month's cut date
    if (today.getDate() < cutDay) {
      currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
    }

    // Set to start of the day
    currentPeriodStart.setHours(0, 0, 0, 0);

    // Calculate previous period's start and end dates
    let previousPeriodStart = new Date(currentPeriodStart);
    previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);

    let previousPeriodEnd = new Date(currentPeriodStart);
    previousPeriodEnd.setHours(0, 0, 0, 0);
    previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1); // End is the day before current period starts

    // Calculate period before previous
    let previous2PeriodStart = new Date(previousPeriodStart);
    previous2PeriodStart.setMonth(previous2PeriodStart.getMonth() - 1);

    let previous2PeriodEnd = new Date(previousPeriodStart);
    previous2PeriodEnd.setHours(0, 0, 0, 0);
    previous2PeriodEnd.setDate(previous2PeriodEnd.getDate() - 1); // End is the day before previous period starts

    // Filter transactions based on selected period
    let filteredTransactions = [];

    if (period === 'current') {
      filteredTransactions = allTransactions.filter(t =>
          t.date >= currentPeriodStart && t.date <= today
      );
    } else if (period === 'previous') {
      filteredTransactions = allTransactions.filter(t =>
          t.date >= previousPeriodStart && t.date <= previousPeriodEnd
      );
    } else if (period === 'previous2') {
      filteredTransactions = allTransactions.filter(t =>
          t.date >= previous2PeriodStart && t.date <= previous2PeriodEnd
      );
    }

    setTransactions(filteredTransactions);
  }, [selectedCard]);

  // Fetch all transactions for the selected card
  useEffect(() => {
    if (!userId || !db || !selectedCardId) return;

    const transactionsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/transactions`);
    const q = query(transactionsCollectionRef, where('cardId', '==', selectedCardId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedTransactions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date ? new Date(doc.data().date) : new Date(doc.data().createdAt?.toDate() || new Date()), // Ensure date is Date object
      })).sort((a, b) => b.date.getTime() - a.date.getTime()); // Sort by date descending

      setAllTransactionsData(fetchedTransactions);
      filterTransactionsByPeriod(fetchedTransactions, selectedPeriod);
      setError('');
    }, (error) => {
      console.error("Error fetching transactions:", error);
      setError("Failed to load transactions.");
    });

    return () => unsubscribe();
  }, [userId, db, selectedCardId, setError, filterTransactionsByPeriod, selectedPeriod]);

  // Filter transactions when period changes
  useEffect(() => {
    if (allTransactionsData.length > 0) {
      filterTransactionsByPeriod(allTransactionsData, selectedPeriod);
    }
  }, [selectedPeriod, selectedCard, allTransactionsData, filterTransactionsByPeriod]);

  // Function to filter transactions by period based on card cut date


  // Calculate current month's expenses and income for budget tracking
  const monthlyExpenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  // const monthlyIncome = transactions
  //     .filter(t => t.type === 'income')
  //     .reduce((sum, t) => sum + t.amount, 0);

  const budgetRemaining = (selectedCard?.budgetLimit || 0) - monthlyExpenses;
  const budgetProgress = selectedCard?.budgetLimit && selectedCard.budgetLimit > 0 ? (monthlyExpenses / selectedCard.budgetLimit) * 100 : 0;

  // Group transactions by category for budget breakdown
  const expensesByCategory = transactions
      .filter(t => t.type === 'expense')
      .reduce((acc, t) => {
        acc[t.category] = (acc[t.category] || 0) + Math.abs(t.amount);
        return acc;
      }, {});


  const handleEditTransaction = (transaction) => {
    // Set form to editing mode
    setIsEditingTransaction(true);
    setEditingTransactionId(transaction.id);

    // Populate form with transaction data
    setNewTransactionAmount(Math.abs(transaction.amount).toString());
    setNewTransactionDescription(transaction.description);
    setNewTransactionType(transaction.type);
    setNewTransactionCategory(transaction.category);
    setNewTransactionDate(transaction.date.toISOString().split('T')[0]);
    setNewTransactionHighlighted(transaction.isHighlighted || false);
    setNewTransactionOwner(transaction.owner || 'me');

    // Load custom split percentage if available
    if (transaction.owner === 'both' && transaction.splitPercentage !== undefined) {
      setCustomSplitPercentage(transaction.splitPercentage);
    } else {
      // Reset to default or card's percentage
      setCustomSplitPercentage(selectedCard && selectedCard.isShared ? (selectedCard.splitPercentage || 70) : 70);
    }

    // Handle installment data if present
    if (transaction.isInstallmentPlanStart) {
      setIsInstallmentPlanStart(true);
      setInstallmentTotalAmount(transaction.installmentTotalAmount.toString());
    } else {
      setIsInstallmentPlanStart(false);
      setInstallmentTotalAmount('');
    }

    // Scroll to the form
    const transactionFormElement = document.getElementById('transactionForm');
    if (transactionFormElement) {
      transactionFormElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (!userId || !selectedCardId) {
      setError('User not authenticated or card not selected.');
      return;
    }

    try {
      const amount = parseFloat(newTransactionAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid positive amount.');
        return;
      }

      // Prepare installment data if applicable
      let installmentData = {};
      if (isInstallmentPlanStart) {
        const totalInstallment = parseFloat(installmentTotalAmount);
        if (isNaN(totalInstallment) || totalInstallment <= 0) {
          setError('Please enter a valid positive installment total amount.');
          return;
        }
        installmentData = {
          isInstallmentPlanStart: true,
          installmentTotalAmount: totalInstallment,
          // monthlyPaymentAmount: amount, // Assuming the 'amount' field is the monthly payment
        };
      }

      if (isEditingTransaction) {
        // Update existing transaction
        const transactionDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/transactions`, editingTransactionId);
        const transactionToUpdate = transactions.find(t => t.id === editingTransactionId);

        if (!transactionToUpdate) {
          setError('Transaction not found. Please try again.');
          return;
        }

        // Calculate the difference in amount to update the card balance
        const oldAmount = transactionToUpdate.amount;
        const newAmount = newTransactionType === 'expense' ? -amount : amount;
        const amountDifference = newAmount - oldAmount;

        // Update transaction
        await updateDoc(transactionDocRef, {
          amount: newAmount,
          description: newTransactionDescription,
          type: newTransactionType,
          category: newTransactionCategory,
          date: newTransactionDate,
          isHighlighted: newTransactionHighlighted,
          owner: newTransactionOwner,
          // Add custom split percentage if it's a shared expense
          ...(newTransactionOwner === 'both' ? { splitPercentage: customSplitPercentage } : {}),
          ...installmentData,
        });

        // Update card balance with the difference
        const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, selectedCardId);
        const newBalance = (selectedCard.currentBalance || 0) + amountDifference;
        await updateDoc(cardDocRef, { currentBalance: newBalance });

        // Reset editing state
        setIsEditingTransaction(false);
        setEditingTransactionId(null);
      } else {
        // Add new transaction
        const transactionsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/transactions`);
        await addDoc(transactionsCollectionRef, {
          cardId: selectedCardId,
          amount: newTransactionType === 'expense' ? -amount : amount,
          description: newTransactionDescription,
          type: newTransactionType,
          category: newTransactionCategory,
          date: newTransactionDate,
          isHighlighted: newTransactionHighlighted,
          createdAt: new Date(),
          owner: newTransactionOwner, // Track ownership for all cards
          // Add custom split percentage if it's a shared expense
          ...(newTransactionOwner === 'both' ? { splitPercentage: customSplitPercentage } : {}),
          ...installmentData, // Add installment specific data
        });

        // Update card balance
        const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, selectedCardId);
        const newBalance = (selectedCard.currentBalance || 0) + (newTransactionType === 'expense' ? -amount : amount);
        await updateDoc(cardDocRef, { currentBalance: newBalance });
      }

      // Clear form
      setNewTransactionAmount('');
      setNewTransactionDescription('');
      setNewTransactionCategory('');
      setNewTransactionDate(new Date().toISOString().split('T')[0]);
      setNewTransactionHighlighted(false);
      setIsInstallmentPlanStart(false); // Reset installment flags
      setInstallmentTotalAmount('');
      setNewTransactionOwner('me');
      setCustomSplitPercentage(70); // Reset custom split percentage to default
      setError('');
    } catch (e) {
      console.error("Error with transaction:", e);
      setError(`Failed to ${isEditingTransaction ? 'update' : 'add'} transaction. Please check your input.`);
    }
  };

  const handleDeleteTransaction = async (transactionId, transactionAmount) => {
    if (!userId || !selectedCardId) {
      setError('User not authenticated.');
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this transaction?");
    if (!confirmed) return;

    try {
      // Delete transaction
      const transactionDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/transactions`, transactionId);
      await deleteDoc(transactionDocRef);

      // Update card balance (reverse the transaction amount)
      const cardDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/cards`, selectedCardId);
      const newBalance = (selectedCard.currentBalance || 0) - transactionAmount;
      await updateDoc(cardDocRef, { currentBalance: newBalance });
      setError('');
    } catch (e) {
      console.error("Error deleting transaction:", e);
      setError("Failed to delete transaction. Please try again.");
    }
  };

  if (!selectedCard) {
    return (
        <div className="bg-white p-8 rounded-lg shadow-xl text-center">
          <p className="text-gray-600">Please select a card from the dashboard to view transactions.</p>
          <button
              onClick={() => setCurrentScreen('dashboard')}
              className="mt-6 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300 ease-in-out"
          >
            Go to Dashboard
          </button>
        </div>
    );
  }

  const transactionCategories = [
    'Food', 'Needs', 'Savings', 'Personal', 'Groceries', 'Utilities', 'Rent/Mortgage', 'Transportation', 'Dining Out',
    'Entertainment', 'Shopping', 'Health', 'Education', 'Salary', 'Freelance',
    'Investment', 'Other Expense', 'Other Income'
  ];

  const highlightedExpenses = transactions.filter(t => t.isHighlighted);

  return (
      <div className="bg-white p-8 rounded-lg shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-indigo-700">Transactions for {selectedCard.name}</h2>
          <button
              onClick={() => {
                setSelectedCardId(null);
                setCurrentScreen('dashboard');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 transition duration-300 ease-in-out"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Card Summary and Financial Health */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="p-6 bg-blue-100 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-blue-800 mb-2">Current Balance</h4>
            <p className="text-3xl font-extrabold text-blue-600">${(selectedCard.currentBalance || 0).toFixed(2)}</p>
          </div>
          {selectedCard.type === 'credit' && (
              <div className="p-6 bg-purple-100 rounded-lg shadow-md">
                <h4 className="text-lg font-semibold text-purple-800 mb-2">Credit Limit</h4>
                <p className="text-3xl font-extrabold text-purple-600">${(selectedCard.creditLimit || 0).toFixed(2)}</p>
                <p className="text-sm text-gray-600 mt-1">Cut Date: <span className="font-semibold">{selectedCard.cutDate || 'N/A'}</span></p>
                <p className="text-sm text-gray-600 mt-1">Payment Due: <span className="font-semibold">{selectedCard.dueDate || 'N/A'}</span></p>
              </div>
          )}
          <div className="p-6 bg-green-100 rounded-lg shadow-md">
            <h4 className="text-lg font-semibold text-green-800 mb-2">Monthly Budget</h4>
            <p className="text-3xl font-extrabold text-green-600">
              ${selectedCard.budgetLimit ? selectedCard.budgetLimit.toFixed(2) : 'N/A'}
            </p>
            {selectedCard.budgetLimit > 0 && (
                <>
                  <p className="text-sm text-gray-600 mt-1">Spent: ${monthlyExpenses.toFixed(2)}</p>
                  <p className={`text-sm mt-1 ${budgetRemaining < 0 ? 'text-red-600 font-bold' : 'text-green-600'}`}>
                    Remaining: ${budgetRemaining.toFixed(2)}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div
                        className={`h-2.5 rounded-full ${budgetProgress > 100 ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(budgetProgress, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{budgetProgress.toFixed(1)}% of budget used</p>
                </>
            )}
          </div>
        </div>

        {/* Budget Breakdown by Category */}
        {Object.keys(expensesByCategory).length > 0 && (
            <div className="mt-8 p-6 bg-gray-50 rounded-lg shadow-inner mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Budget Breakdown (This Month)</h3>
              <ul className="space-y-2">
                {Object.entries(expensesByCategory).map(([category, amount]) => (
                    <li key={category} className="flex justify-between items-center text-gray-700">
                      <span className="font-medium">{category}:</span>
                      <span className="font-semibold">${(amount || 0).toFixed(2)}</span>
                    </li>
                ))}
              </ul>
            </div>
        )}


        {/* Add/Edit Transaction Form */}
        <div id="transactionForm" className="mt-8 p-6 bg-gray-50 rounded-lg shadow-inner mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {isEditingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
          </h3>
          <form onSubmit={handleAddTransaction} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="transactionType" className="block text-gray-700 text-sm font-semibold mb-2">
                Type
              </label>
              <select
                  id="transactionType"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newTransactionType}
                  onChange={(e) => setNewTransactionType(e.target.value)}
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div>
              <label htmlFor="transactionAmount" className="block text-gray-700 text-sm font-semibold mb-2">
                Amount ($)
              </label>
              <input
                  type="number"
                  id="transactionAmount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newTransactionAmount}
                  onChange={(e) => setNewTransactionAmount(e.target.value)}
                  placeholder="e.g., 25.50"
                  min="0.01"
                  step="0.01"
                  required
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="transactionDescription" className="block text-gray-700 text-sm font-semibold mb-2">
                Description
              </label>
              <input
                  type="text"
                  id="transactionDescription"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newTransactionDescription}
                  onChange={(e) => setNewTransactionDescription(e.target.value)}
                  placeholder="e.g., Coffee at Starbucks"
                  required
              />
            </div>
            <div>
              <label htmlFor="transactionCategory" className="block text-gray-700 text-sm font-semibold mb-2">
                Category
              </label>
              <select
                  id="transactionCategory"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newTransactionCategory}
                  onChange={(e) => setNewTransactionCategory(e.target.value)}
                  required
              >
                <option value="">Select a Category</option>
                {transactionCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="transactionDate" className="block text-gray-700 text-sm font-semibold mb-2">
                Date
              </label>
              <input
                  type="date"
                  id="transactionDate"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newTransactionDate}
                  onChange={(e) => setNewTransactionDate(e.target.value)}
                  required
              />
            </div>
            <div className="flex items-center md:col-span-2 mt-2">
              <input
                  type="checkbox"
                  id="highlightTransaction"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  checked={newTransactionHighlighted}
                  onChange={(e) => setNewTransactionHighlighted(e.target.checked)}
              />
              <label htmlFor="highlightTransaction" className="ml-2 block text-gray-700 text-sm font-semibold">
                Highlight this expense
              </label>
            </div>

            {/* New: Is Installment Plan Start checkbox */}
            <div className="flex items-center md:col-span-2 mt-2">
              <input
                  type="checkbox"
                  id="isInstallmentPlanStart"
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  checked={isInstallmentPlanStart}
                  onChange={(e) => setIsInstallmentPlanStart(e.target.checked)}
              />
              <label htmlFor="isInstallmentPlanStart" className="ml-2 block text-gray-700 text-sm font-semibold">
                Mark as start of Installment Plan
              </label>
            </div>
            {isInstallmentPlanStart && (
                <div className="md:col-span-2">
                  <label htmlFor="installmentTotalAmount" className="block text-gray-700 text-sm font-semibold mb-2">
                    Total Installment Amount ($)
                  </label>
                  <input
                      type="number"
                      id="installmentTotalAmount"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={installmentTotalAmount}
                      onChange={(e) => setInstallmentTotalAmount(e.target.value)}
                      placeholder="e.g., 500"
                      min="0.01"
                      step="0.01"
                      required
                  />
                </div>
            )}

            {/* Transaction Owner Selection */}
            <div className="md:col-span-2 mt-4 p-4 bg-pink-50 rounded-lg border border-pink-200">
              <label className="block text-gray-700 text-sm font-semibold mb-3">
                {selectedCard && selectedCard.isShared 
                  ? "Who is this transaction for?" 
                  : "Is this a transaction your partner should pay you for?"}
              </label>
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="ownerMe"
                    name="transactionOwner"
                    value="me"
                    checked={newTransactionOwner === 'me'}
                    onChange={() => setNewTransactionOwner('me')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="ownerMe" className="ml-2 block text-sm text-gray-700">
                    {selectedCard && selectedCard.isShared 
                      ? (isPartner ? `Just me (${100 - (selectedCard.splitPercentage || 0)}%)` : `Just me (${selectedCard.splitPercentage}%)`)
                      : "Just me (I'll pay for this)"}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="ownerOther"
                    name="transactionOwner"
                    value="other"
                    checked={newTransactionOwner === 'other'}
                    onChange={() => setNewTransactionOwner('other')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="ownerOther" className="ml-2 block text-sm text-gray-700">
                    {selectedCard && selectedCard.isShared 
                      ? (isPartner ? `Just my partner (${selectedCard.splitPercentage}%)` : `Just my partner (${100 - (selectedCard.splitPercentage || 0)}%)`)
                      : "My partner (they should pay me back)"}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="ownerBoth"
                    name="transactionOwner"
                    value="both"
                    checked={newTransactionOwner === 'both'}
                    onChange={() => setNewTransactionOwner('both')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="ownerBoth" className="ml-2 block text-sm text-gray-700">
                    {selectedCard && selectedCard.isShared 
                      ? "Shared expense (split)" 
                      : "Both of us (we'll split it)"}
                  </label>
                </div>
              </div>

              {/* Custom Split Percentage (only shown when "both" is selected) */}
              {newTransactionOwner === 'both' && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <label className="block text-gray-700 text-sm font-semibold mb-3">
                    Custom Split Percentage
                  </label>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">My share:</span>
                    <div className="flex-1 flex items-center">
                      <input
                        type="range"
                        id="customSplitPercentage"
                        className="w-full mr-4"
                        min="0"
                        max="100"
                        step="5"
                        value={customSplitPercentage}
                        onChange={(e) => setCustomSplitPercentage(parseInt(e.target.value, 10))}
                      />
                      <div className="flex items-center">
                        <input
                          type="number"
                          className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={customSplitPercentage}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 0 && value <= 100) {
                              setCustomSplitPercentage(value);
                            }
                          }}
                          min="0"
                          max="100"
                        />
                        <span className="ml-1">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2 text-sm text-gray-600">
                    <span>My share: {customSplitPercentage}%</span>
                    <span>Partner's share: {100 - customSplitPercentage}%</span>
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex space-x-4">
              {isEditingTransaction && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingTransaction(false);
                    setEditingTransactionId(null);
                    setNewTransactionAmount('');
                    setNewTransactionDescription('');
                    setNewTransactionCategory('');
                    setNewTransactionDate(new Date().toISOString().split('T')[0]);
                    setNewTransactionHighlighted(false);
                    setIsInstallmentPlanStart(false);
                    setInstallmentTotalAmount('');
                    setNewTransactionOwner('me');
                    setCustomSplitPercentage(70); // Reset custom split percentage to default
                  }}
                  className="w-full py-3 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg"
              >
                {isEditingTransaction ? 'Update Transaction' : 'Add Transaction'}
              </button>
            </div>
          </form>
        </div>

        {/* Highlighted Expenses */}
        {highlightedExpenses.length > 0 && (
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg shadow-inner">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4">Highlighted Expenses</h3>
              <ul className="divide-y divide-yellow-200">
                {highlightedExpenses.map((t) => (
                    <li key={t.id} className="py-3 flex justify-between items-center text-yellow-900">
                <span>
                  {t.description} ({t.category}) on {t.date.toLocaleDateString()}
                </span>
                      <span className="font-semibold text-lg">${Math.abs(t.amount || 0).toFixed(2)}</span>
                    </li>
                ))}
              </ul>
            </div>
        )}


        {/* Period Tabs */}
        <div className="mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setSelectedPeriod('current')}
              className={`py-2 px-4 font-medium text-sm focus:outline-none ${
                selectedPeriod === 'current'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Current Period
            </button>
            <button
              onClick={() => setSelectedPeriod('previous')}
              className={`py-2 px-4 font-medium text-sm focus:outline-none ${
                selectedPeriod === 'previous'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Previous Period
            </button>
            <button
              onClick={() => setSelectedPeriod('previous2')}
              className={`py-2 px-4 font-medium text-sm focus:outline-none ${
                selectedPeriod === 'previous2'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Previous to Previous
            </button>
          </div>
        </div>

        {/* Period Transactions List */}
        <h3 className="text-2xl font-semibold text-gray-800 mb-2">
          {selectedPeriod === 'current' && 'Current Period Transactions'}
          {selectedPeriod === 'previous' && 'Previous Period Transactions'}
          {selectedPeriod === 'previous2' && 'Previous to Previous Period Transactions'}
        </h3>

        {selectedCard && selectedCard.cutDate && (
          <div className="text-sm text-gray-600 mb-4">
            {selectedPeriod === 'current' && (
              <>
                {(() => {
                  const today = new Date();
                  const cutDay = parseInt(selectedCard.cutDate.split('-')[2], 10);
                  let periodStart = new Date(today);
                  periodStart.setDate(cutDay);
                  if (today.getDate() < cutDay) {
                    periodStart.setMonth(periodStart.getMonth() - 1);
                  }
                  return `${periodStart.toLocaleDateString()} to ${today.toLocaleDateString()}`;
                })()}
              </>
            )}
            {selectedPeriod === 'previous' && (
              <>
                {(() => {
                  const today = new Date();
                  const cutDay = parseInt(selectedCard.cutDate.split('-')[2], 10);
                  let currentPeriodStart = new Date(today);
                  currentPeriodStart.setDate(cutDay);
                  if (today.getDate() < cutDay) {
                    currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
                  }
                  let previousPeriodStart = new Date(currentPeriodStart);
                  previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
                  let previousPeriodEnd = new Date(currentPeriodStart);
                  previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
                  return `${previousPeriodStart.toLocaleDateString()} to ${previousPeriodEnd.toLocaleDateString()}`;
                })()}
              </>
            )}
            {selectedPeriod === 'previous2' && (
              <>
                {(() => {
                  const today = new Date();
                  const cutDay = parseInt(selectedCard.cutDate.split('-')[2], 10);
                  let currentPeriodStart = new Date(today);
                  currentPeriodStart.setDate(cutDay);
                  if (today.getDate() < cutDay) {
                    currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
                  }
                  let previousPeriodStart = new Date(currentPeriodStart);
                  previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
                  let previous2PeriodStart = new Date(previousPeriodStart);
                  previous2PeriodStart.setMonth(previous2PeriodStart.getMonth() - 1);
                  let previous2PeriodEnd = new Date(previousPeriodStart);
                  previous2PeriodEnd.setDate(previous2PeriodEnd.getDate() - 1);
                  return `${previous2PeriodStart.toLocaleDateString()} to ${previous2PeriodEnd.toLocaleDateString()}`;
                })()}
              </>
            )}
          </div>
        )}

        {transactions.length === 0 ? (
            <p className="text-gray-600">No transactions recorded for this period.</p>
        ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg shadow-md overflow-hidden">
                  <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                  {transactions.map((t) => (
                      <tr key={t.id} className={`${t.isHighlighted ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.date.toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              t.type === 'expense' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {t.type.charAt(0).toUpperCase() + t.type.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {t.owner === 'me' && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Me
                            </span>
                          )}
                          {t.owner === 'other' && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-pink-100 text-pink-800">
                              {isPartner ? "Partner" : "Partner"}
                            </span>
                          )}
                          {t.owner === 'both' && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                              Shared
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <span className={`${t.type === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                            {t.type === 'expense' ? '-' : '+'}${Math.abs(t.amount || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => handleEditTransaction(t)}
                                className="text-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-300 ease-in-out"
                            >
                              Edit
                            </button>
                            <button
                                onClick={() => handleDeleteTransaction(t.id, t.amount)}
                                className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition duration-300 ease-in-out"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>

              {/* Payment Summary */}
              <div className="mt-8 p-6 bg-pink-50 rounded-lg shadow-inner">
                <h3 className="text-xl font-semibold text-pink-800 mb-4">
                  {selectedCard && selectedCard.isShared ? "Payment Summary" : "Partner Payment Summary"}
                </h3>

                {(() => {
                  // Calculate payment summary
                  const expenseTransactions = transactions.filter(t => t.type === 'expense');

                  // Calculate totals by owner
                  const myExpenses = expenseTransactions
                    .filter(t => t.owner === 'me')
                    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

                  const herExpenses = expenseTransactions
                    .filter(t => t.owner === 'other')
                    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

                  // For all cards, we need to handle "both" expenses that need to be split
                  // Get shared expenses and calculate portions using custom percentages
                  const sharedTransactions = expenseTransactions.filter(t => t.owner === 'both');

                  // Calculate total shared expenses
                  const sharedExpenses = sharedTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);

                  // Calculate my portion and partner's portion using custom percentages for each transaction
                  let mySharedPortion = 0;
                  let herSharedPortion = 0;

                  sharedTransactions.forEach(transaction => {
                    // Use transaction's custom percentage if available, otherwise use card's percentage or default
                    const myPercentage = transaction.splitPercentage !== undefined 
                      ? transaction.splitPercentage 
                      : (selectedCard && selectedCard.isShared ? (selectedCard.splitPercentage || 70) : 70);

                    const transactionAmount = Math.abs(transaction.amount);
                    mySharedPortion += (transactionAmount * myPercentage) / 100;
                    herSharedPortion += (transactionAmount * (100 - myPercentage)) / 100;
                  });

                  // Calculate average percentages for display purposes
                  const myPercentage = sharedExpenses > 0 
                    ? Math.round((mySharedPortion / sharedExpenses) * 100) 
                    : (selectedCard && selectedCard.isShared ? (selectedCard.splitPercentage || 70) : 70);
                  const herPercentage = 100 - myPercentage;

                  // Calculate total amounts each person is responsible for
                  const myTotalResponsibility = myExpenses + mySharedPortion;
                  const herTotalResponsibility = herExpenses + herSharedPortion;

                  // Calculate total expenses
                  const totalExpenses = myExpenses + herExpenses + sharedExpenses;

                  // For non-shared cards, we calculate what partner owes including their portion of shared expenses
                  const partnerOwesMe = !selectedCard?.isShared ? herExpenses + herSharedPortion : 0;

                  return (
                    <div>
                      {selectedCard && selectedCard.isShared ? (
                        // Display for shared cards
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="p-4 bg-white rounded-lg shadow-sm">
                              <h4 className="text-lg font-semibold text-blue-800 mb-2">My Expenses</h4>
                              <p className="text-2xl font-bold text-blue-600">${myExpenses.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">Expenses I'm solely responsible for</p>
                            </div>

                            <div className="p-4 bg-white rounded-lg shadow-sm">
                              <h4 className="text-lg font-semibold text-pink-800 mb-2">{isPartner ? "Partner's Expenses" : "Partner's Expenses"}</h4>
                              <p className="text-2xl font-bold text-pink-600">${herExpenses.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">{isPartner ? "Expenses my partner is solely responsible for" : "Expenses my partner is solely responsible for"}</p>
                            </div>

                            <div className="p-4 bg-white rounded-lg shadow-sm">
                              <h4 className="text-lg font-semibold text-purple-800 mb-2">Shared Expenses</h4>
                              <p className="text-2xl font-bold text-purple-600">${sharedExpenses.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">
                                {isPartner 
                                  ? `Expenses we're splitting ${herPercentage}%/${myPercentage}% (using custom percentages)` 
                                  : `Expenses we're splitting ${myPercentage}%/${herPercentage}% (using custom percentages)`}
                              </p>
                            </div>
                          </div>

                          <div className="p-4 bg-white rounded-lg shadow-sm mb-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-2">Split Calculation</h4>
                            <div className="space-y-2">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">My portion of shared expenses:</span> ${mySharedPortion.toFixed(2)} (calculated using custom percentages)
                              </p>
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Partner's portion of shared expenses:</span> ${herSharedPortion.toFixed(2)} (calculated using custom percentages)
                              </p>
                              <p className="text-xs text-gray-500 mt-2 italic">
                                Note: Each shared expense uses its own custom percentage split.
                              </p>
                            </div>
                          </div>

                          <div className="p-4 bg-indigo-50 rounded-lg shadow-sm">
                            <h4 className="text-lg font-semibold text-indigo-800 mb-2">Payment Due</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-gray-700 mb-1">
                                  <span className="font-medium">My total responsibility:</span> ${myTotalResponsibility.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Partner's total responsibility:</span> ${herTotalResponsibility.toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-700 mt-2">
                                  <span className="font-medium">Total expenses:</span> ${totalExpenses.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        // Display for non-shared cards
                        <div className="p-4 bg-indigo-50 rounded-lg shadow-sm">
                          <h4 className="text-lg font-semibold text-indigo-800 mb-4">Partner Expenses Summary</h4>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="p-4 bg-white rounded-lg shadow-sm">
                              <h4 className="text-lg font-semibold text-blue-800 mb-2">My Expenses</h4>
                              <p className="text-2xl font-bold text-blue-600">${myExpenses.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">Expenses I'm solely responsible for</p>
                            </div>

                            <div className="p-4 bg-white rounded-lg shadow-sm">
                              <h4 className="text-lg font-semibold text-pink-800 mb-2">Partner's Expenses</h4>
                              <p className="text-2xl font-bold text-pink-600">${herExpenses.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">Expenses my partner needs to pay me back for</p>
                            </div>

                            {sharedExpenses > 0 && (
                              <div className="p-4 bg-white rounded-lg shadow-sm">
                                <h4 className="text-lg font-semibold text-purple-800 mb-2">Shared Expenses</h4>
                                <p className="text-2xl font-bold text-purple-600">${sharedExpenses.toFixed(2)}</p>
                                <p className="text-sm text-gray-600">Expenses we're splitting {myPercentage}%/{herPercentage}% (using custom percentages)</p>
                              </div>
                            )}
                          </div>

                          <div className="bg-white p-4 rounded-lg border border-indigo-200 mt-4">
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-700">Your partner owes you:</p>
                              <p className="text-2xl font-bold text-green-600">
                                ${partnerOwesMe.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </>
        )}
      </div>
  );
}

// Goals Screen Component
function GoalsScreen() {
  // eslint-disable-next-line no-unused-vars
  const { userId, db, setCurrentScreen, setError } = useContext(AppContext);
  const [goals, setGoals] = useState([]);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalTargetAmount, setNewGoalTargetAmount] = useState('');
  const [newGoalDeadline, setNewGoalDeadline] = useState('');
  const [newGoalNotes, setNewGoalNotes] = useState('');
  const [isAddingGoal, setIsAddingGoal] = useState(false);

  // Fetch goals from Firestore
  useEffect(() => {
    if (!userId || !db) return;

    const goalsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/goals`);
    const unsubscribe = onSnapshot(goalsCollectionRef, (snapshot) => {
      const fetchedGoals = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGoals(fetchedGoals);
      setError('');
    }, (error) => {
      console.error("Error fetching goals:", error);
      setError("Failed to load goals. Please refresh the page.");
    });

    return () => unsubscribe();
  }, [userId, db, setError]);

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError('User not authenticated.');
      return;
    }

    try {
      const goalsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/goals`);
      await addDoc(goalsCollectionRef, {
        name: newGoalName,
        targetAmount: parseFloat(newGoalTargetAmount || 0),
        currentAmount: 0, // Goals start at 0 progress
        deadline: newGoalDeadline,
        notes: newGoalNotes,
        status: 'in-progress',
        createdAt: new Date(),
      });
      setIsAddingGoal(false);
      setNewGoalName('');
      setNewGoalTargetAmount('');
      setNewGoalDeadline('');
      setNewGoalNotes('');
      setError('');
    } catch (e) {
      console.error("Error adding goal:", e);
      setError("Failed to add goal. Please check your input.");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }
    const confirmed = window.confirm("Are you sure you want to delete this goal?");
    if (!confirmed) return;

    try {
      const goalDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/goals`, goalId);
      await deleteDoc(goalDocRef);
      setError('');
    } catch (e) {
      console.error("Error deleting goal:", e);
      setError("Failed to delete goal. Please try again.");
    }
  };

  const handleUpdateGoalProgress = async (goalId, newProgress) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }
    const goalRef = doc(db, `artifacts/${__app_id}/users/${userId}/goals`, goalId);
    try {
      await updateDoc(goalRef, { currentAmount: parseFloat(newProgress) });
      setError('');
    } catch (e) {
      console.error("Error updating goal progress:", e);
      setError("Failed to update goal progress.");
    }
  };

  return (
      <div className="bg-white p-8 rounded-lg shadow-xl">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6">Your Financial Goals</h2>

        <button
            onClick={() => setIsAddingGoal(!isAddingGoal)}
            className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-6"
        >
          {isAddingGoal ? 'Cancel Add Goal' : 'Add New Goal'}
        </button>

        {isAddingGoal && (
            <div className="mt-8 p-6 bg-gray-50 rounded-lg shadow-inner mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Add New Goal</h3>
              <form onSubmit={handleAddGoal} className="space-y-4">
                <div>
                  <label htmlFor="goalName" className="block text-gray-700 text-sm font-semibold mb-2">
                    Goal Name
                  </label>
                  <input
                      type="text"
                      id="goalName"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newGoalName}
                      onChange={(e) => setNewGoalName(e.target.value)}
                      placeholder="e.g., Down Payment for House"
                      required
                  />
                </div>
                <div>
                  <label htmlFor="goalTargetAmount" className="block text-gray-700 text-sm font-semibold mb-2">
                    Target Amount ($)
                  </label>
                  <input
                      type="number"
                      id="goalTargetAmount"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newGoalTargetAmount}
                      onChange={(e) => setNewGoalTargetAmount(e.target.value)}
                      placeholder="e.g., 20000"
                      min="0.01"
                      step="0.01"
                      required
                  />
                </div>
                <div>
                  <label htmlFor="goalDeadline" className="block text-gray-700 text-sm font-semibold mb-2">
                    Deadline (Optional)
                  </label>
                  <input
                      type="date"
                      id="goalDeadline"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newGoalDeadline}
                      onChange={(e) => setNewGoalDeadline(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="goalNotes" className="block text-gray-700 text-sm font-semibold mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                      id="goalNotes"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newGoalNotes}
                      onChange={(e) => setNewGoalNotes(e.target.value)}
                      placeholder="e.g., Saving for a trip to Japan."
                      rows="3"
                  ></textarea>
                </div>
                <button
                    type="submit"
                    className="w-full py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-sm"
                >
                  Create Goal
                </button>
              </form>
            </div>
        )}

        {goals.length === 0 ? (
            <p className="text-gray-600">No goals added yet. Set your financial aspirations!</p>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {goals.map((goal) => {
                const progress = goal.targetAmount && goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0;
                const remainingAmount = goal.targetAmount - goal.currentAmount;
                const isCompleted = goal.currentAmount >= goal.targetAmount;
                return (
                    <div key={goal.id} className="bg-white p-5 rounded-lg shadow-md border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className={`text-xl font-bold ${isCompleted ? 'text-green-700' : 'text-indigo-600'}`}>{goal.name}</h4>
                        <button
                            onClick={() => handleDeleteGoal(goal.id)}
                            className="px-2 py-1 bg-red-400 text-white rounded-md text-xs hover:bg-red-500"
                        >
                          Delete
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm">Target: <span className="font-semibold">${goal.targetAmount.toFixed(2)}</span></p>
                      <p className="text-gray-700 text-sm">Current: <span className="font-semibold">${goal.currentAmount.toFixed(2)}</span></p>
                      {goal.deadline && (
                          <p className="text-gray-700 text-sm">Deadline: <span className="font-semibold">{goal.deadline}</span></p>
                      )}
                      {goal.notes && (
                          <p className="text-gray-600 text-xs italic mt-1">"{goal.notes}"</p>
                      )}
                      <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                        <div
                            className={`h-3 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{progress.toFixed(1)}% complete</p>
                      {!isCompleted && remainingAmount > 0 && (
                          <p className="text-sm font-semibold text-gray-700 mt-2">
                            Remaining: ${remainingAmount.toFixed(2)}
                          </p>
                      )}
                      <div className="mt-4 flex items-center space-x-2">
                        <label htmlFor={`progress-${goal.id}`} className="text-sm text-gray-700">Update Progress:</label>
                        <input
                            type="number"
                            id={`progress-${goal.id}`}
                            value={goal.currentAmount}
                            onChange={(e) => handleUpdateGoalProgress(goal.id, e.target.value)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            min="0"
                            step="0.01"
                        />
                      </div>
                    </div>
                );
              })}
            </div>
        )}
      </div>
  );
}

// History Screen Component
function HistoryScreen() {
  const { userId, db, setError } = useContext(AppContext);
  const [transactions, setTransactions] = useState([]);
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);

  // State for editing transactions
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [newTransactionAmount, setNewTransactionAmount] = useState('');
  const [newTransactionDescription, setNewTransactionDescription] = useState('');
  const [newTransactionType, setNewTransactionType] = useState('expense');
  const [newTransactionCategory, setNewTransactionCategory] = useState('');
  const [newTransactionDate, setNewTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [newTransactionHighlighted, setNewTransactionHighlighted] = useState(false);

  const processChartData = useCallback((allTransactions) => {
    // Filter transactions for the selected month and year
    const filteredTransactions = allTransactions.filter(t => {
      return t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear;
    });

    // Group by day and calculate daily totals
    const dailyData = {};
    filteredTransactions.forEach(t => {
      const day = t.date.getDate();
      if (!dailyData[day]) {
        dailyData[day] = { day, income: 0, expense: 0 };
      }

      if (t.type === 'income') {
        dailyData[day].income += t.amount;
      } else if (t.type === 'expense') {
        dailyData[day].expense += Math.abs(t.amount);
      }
    });

    // Convert to array and sort by day
    const chartDataArray = Object.values(dailyData).sort((a, b) => a.day - b.day);
    setChartData(chartDataArray);
  }, [filterMonth, filterYear]);

  // Fetch all transactions for history
  useEffect(() => {
    if (!userId || !db) return;

    const transactionsCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/transactions`);
    const unsubscribe = onSnapshot(transactionsCollectionRef, (snapshot) => {
      const fetchedTransactions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date ? new Date(doc.data().date) : new Date(doc.data().createdAt?.toDate() || new Date()),
      })).sort((a, b) => b.date.getTime() - a.date.getTime()); // Sort by date descending

      setTransactions(fetchedTransactions);

      // Process data for chart
      processChartData(fetchedTransactions);

      setError('');
    }, (error) => {
      console.error("Error fetching transactions history:", error);
      setError("Failed to load transaction history. Please refresh the page.");
    });

    return () => unsubscribe();
  }, [userId, db, setError, filterMonth, filterYear, processChartData]);

  // Get month name
  const getMonthName = (monthIndex) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[monthIndex];
  };

  // Handle month change
  const handlePreviousMonth = () => {
    if (filterMonth === 0) {
      setFilterMonth(11);
      setFilterYear(filterYear - 1);
    } else {
      setFilterMonth(filterMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (filterMonth === 11) {
      setFilterMonth(0);
      setFilterYear(filterYear + 1);
    } else {
      setFilterMonth(filterMonth + 1);
    }
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    // Set form to editing mode
    setIsEditingTransaction(true);
    setEditingTransactionId(transaction.id);

    // Populate form with transaction data
    setNewTransactionAmount(Math.abs(transaction.amount).toString());
    setNewTransactionDescription(transaction.description);
    setNewTransactionType(transaction.type);
    setNewTransactionCategory(transaction.category);
    setNewTransactionDate(transaction.date.toISOString().split('T')[0]);
    setNewTransactionHighlighted(transaction.isHighlighted || false);

    // Scroll to the form
    const transactionFormElement = document.getElementById('transactionForm');
    if (transactionFormElement) {
      transactionFormElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle update transaction
  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError('User not authenticated.');
      return;
    }

    try {
      const amount = parseFloat(newTransactionAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid positive amount.');
        return;
      }

      // Update existing transaction
      const transactionDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/transactions`, editingTransactionId);
      const transactionToUpdate = transactions.find(t => t.id === editingTransactionId);

      if (!transactionToUpdate) {
        setError('Transaction not found. Please try again.');
        return;
      }

      // Calculate the new amount based on transaction type
      const newAmount = newTransactionType === 'expense' ? -amount : amount;

      // Update transaction
      await updateDoc(transactionDocRef, {
        amount: newAmount,
        description: newTransactionDescription,
        type: newTransactionType,
        category: newTransactionCategory,
        date: newTransactionDate,
        isHighlighted: newTransactionHighlighted,
      });

      // Reset editing state
      setIsEditingTransaction(false);
      setEditingTransactionId(null);
      setNewTransactionAmount('');
      setNewTransactionDescription('');
      setNewTransactionCategory('');
      setNewTransactionDate(new Date().toISOString().split('T')[0]);
      setNewTransactionHighlighted(false);
      setError('');
    } catch (e) {
      console.error("Error updating transaction:", e);
      setError("Failed to update transaction. Please check your input.");
    }
  };

  // Handle delete individual transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this transaction?");
    if (!confirmed) return;

    try {
      // Delete transaction
      const transactionDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/transactions`, transactionId);
      await deleteDoc(transactionDocRef);
      setError('');
    } catch (e) {
      console.error("Error deleting transaction:", e);
      setError("Failed to delete transaction. Please try again.");
    }
  };

  // Handle delete transactions for the selected month
  const handleDeleteMonthTransactions = async () => {
    if (!userId || !db) {
      setError('User not authenticated.');
      return;
    }

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Check if trying to delete current month's transactions
    if (filterMonth === currentMonth && filterYear === currentYear) {
      setError("Cannot delete transactions from the current month.");
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete ALL transactions from ${getMonthName(filterMonth)} ${filterYear}? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      const transactionsToDelete = transactions.filter(t => 
        t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear
      );

      if (transactionsToDelete.length === 0) {
        setError("No transactions found for the selected month.");
        return;
      }

      const deletePromises = transactionsToDelete.map(transaction => {
        const transactionDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/transactions`, transaction.id);
        return deleteDoc(transactionDocRef);
      });

      await Promise.all(deletePromises);
      setError('');
      // Success message
      alert(`Successfully deleted ${transactionsToDelete.length} transactions from ${getMonthName(filterMonth)} ${filterYear}.`);
    } catch (e) {
      console.error("Error deleting transactions:", e);
      setError("Failed to delete transactions. Please try again.");
    }
  };

  // Calculate totals
  const totalIncome = transactions
    .filter(t => t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear && t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpense = transactions
    .filter(t => t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear && t.type === 'expense')
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const netSavings = totalIncome - totalExpense;

  return (
    <div className="bg-white p-8 rounded-lg shadow-xl">
      <h2 className="text-3xl font-bold text-indigo-700 mb-6">Transaction History</h2>

      {/* Month selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button 
            onClick={handlePreviousMonth}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none mr-2"
          >
            &larr; Previous
          </button>
          <h3 className="text-xl font-semibold text-gray-800 mx-4">
            {getMonthName(filterMonth)} {filterYear}
          </h3>
          <button 
            onClick={handleNextMonth}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none ml-2"
          >
            Next &rarr;
          </button>
        </div>

        {/* Delete button - only enabled for previous months */}
        {(filterMonth !== new Date().getMonth() || filterYear !== new Date().getFullYear()) && (
          <button 
            onClick={handleDeleteMonthTransactions}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition duration-300 ease-in-out"
          >
            Delete All Transactions
          </button>
        )}
      </div>

      {/* Monthly summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="p-6 bg-green-100 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold text-green-800 mb-2">Total Income</h4>
          <p className="text-3xl font-extrabold text-green-600">${totalIncome.toFixed(2)}</p>
        </div>
        <div className="p-6 bg-red-100 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold text-red-800 mb-2">Total Expenses</h4>
          <p className="text-3xl font-extrabold text-red-600">${totalExpense.toFixed(2)}</p>
        </div>
        <div className="p-6 bg-blue-100 rounded-lg shadow-md">
          <h4 className="text-lg font-semibold text-blue-800 mb-2">Net Savings</h4>
          <p className={`text-3xl font-extrabold ${netSavings >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            ${netSavings.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="mb-8 p-4 bg-gray-50 rounded-lg shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Monthly Cash Flow</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" label={{ value: 'Day of Month', position: 'insideBottomRight', offset: -10 }} />
                <YAxis label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => ['$' + value.toFixed(2)]} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#10B981" name="Income" />
                <Line type="monotone" dataKey="expense" stroke="#EF4444" name="Expense" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <p className="text-gray-600 mb-8">No transaction data available for this month.</p>
      )}

      {/* Edit Transaction Form */}
      {isEditingTransaction && (
        <div id="transactionForm" className="mb-8 p-6 bg-gray-50 rounded-lg shadow-inner">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Edit Transaction</h3>
          <form onSubmit={handleUpdateTransaction} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="transactionType" className="block text-gray-700 text-sm font-semibold mb-2">
                Type
              </label>
              <select
                id="transactionType"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newTransactionType}
                onChange={(e) => setNewTransactionType(e.target.value)}
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div>
              <label htmlFor="transactionAmount" className="block text-gray-700 text-sm font-semibold mb-2">
                Amount ($)
              </label>
              <input
                type="number"
                id="transactionAmount"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newTransactionAmount}
                onChange={(e) => setNewTransactionAmount(e.target.value)}
                placeholder="e.g., 25.50"
                min="0.01"
                step="0.01"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="transactionDescription" className="block text-gray-700 text-sm font-semibold mb-2">
                Description
              </label>
              <input
                type="text"
                id="transactionDescription"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newTransactionDescription}
                onChange={(e) => setNewTransactionDescription(e.target.value)}
                placeholder="e.g., Coffee at Starbucks"
                required
              />
            </div>
            <div>
              <label htmlFor="transactionCategory" className="block text-gray-700 text-sm font-semibold mb-2">
                Category
              </label>
              <select
                id="transactionCategory"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newTransactionCategory}
                onChange={(e) => setNewTransactionCategory(e.target.value)}
                required
              >
                <option value="">Select a Category</option>
                {['Food', 'Needs', 'Savings', 'Personal', 'Groceries', 'Utilities', 'Rent/Mortgage', 'Transportation', 'Dining Out',
                  'Entertainment', 'Shopping', 'Health', 'Education', 'Salary', 'Freelance',
                  'Investment', 'Other Expense', 'Other Income'].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="transactionDate" className="block text-gray-700 text-sm font-semibold mb-2">
                Date
              </label>
              <input
                type="date"
                id="transactionDate"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newTransactionDate}
                onChange={(e) => setNewTransactionDate(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center md:col-span-2 mt-2">
              <input
                type="checkbox"
                id="highlightTransaction"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                checked={newTransactionHighlighted}
                onChange={(e) => setNewTransactionHighlighted(e.target.checked)}
              />
              <label htmlFor="highlightTransaction" className="ml-2 block text-gray-700 text-sm font-semibold">
                Highlight this expense
              </label>
            </div>
            <div className="md:col-span-2 flex space-x-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditingTransaction(false);
                  setEditingTransactionId(null);
                  setNewTransactionAmount('');
                  setNewTransactionDescription('');
                  setNewTransactionCategory('');
                  setNewTransactionDate(new Date().toISOString().split('T')[0]);
                  setNewTransactionHighlighted(false);
                }}
                className="w-full py-3 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg"
              >
                Update Transaction
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Transactions list */}
      <h3 className="text-2xl font-semibold text-gray-800 mb-4">Transactions</h3>
      {transactions.filter(t => t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear).length === 0 ? (
        <p className="text-gray-600">No transactions recorded for this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-md overflow-hidden">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions
                .filter(t => t.date.getMonth() === filterMonth && t.date.getFullYear() === filterYear)
                .map((t) => (
                  <tr key={t.id} className={`${t.isHighlighted ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.date.toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.cardName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <span className={`${t.type === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                        {t.type === 'expense' ? '-' : '+'}${Math.abs(t.amount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditTransaction(t)}
                          className="text-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-300 ease-in-out"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTransaction(t.id)}
                          className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition duration-300 ease-in-out"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Reminders Screen Component
function RemindersScreen() {
  const { userId, db, setError } = useContext(AppContext);
  const [reminders, setReminders] = useState([]);
  const [newReminderTitle, setNewReminderTitle] = useState('');
  const [newReminderDescription, setNewReminderDescription] = useState('');
  const [newReminderDueDate, setNewReminderDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [isAddingReminder, setIsAddingReminder] = useState(false);

  // Fetch all reminders
  useEffect(() => {
    if (!userId || !db) return;

    const remindersCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/reminders`);
    const unsubscribe = onSnapshot(remindersCollectionRef, (snapshot) => {
      const fetchedReminders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })).sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()); // Sort by due date

      setReminders(fetchedReminders);
      setError('');
    }, (error) => {
      console.error("Error fetching reminders:", error);
      setError("Failed to load reminders. Please refresh the page.");
    });

    return () => unsubscribe();
  }, [userId, db, setError]);

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError('User not authenticated.');
      return;
    }

    try {
      const remindersCollectionRef = collection(db, `artifacts/${__app_id}/users/${userId}/reminders`);
      await addDoc(remindersCollectionRef, {
        title: newReminderTitle,
        description: newReminderDescription,
        dueDate: newReminderDueDate,
        isCompleted: false,
        type: 'manual', // Manually added reminder
        relatedCardId: null, // No related card for manual reminders
        createdAt: new Date(),
      });
      setIsAddingReminder(false);
      setNewReminderTitle('');
      setNewReminderDescription('');
      setNewReminderDueDate(new Date().toISOString().split('T')[0]);
      setError('');
    } catch (e) {
      console.error("Error adding reminder:", e);
      setError("Failed to add reminder. Please check your input.");
    }
  };

  const handleToggleComplete = async (reminderId, currentStatus) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }
    const reminderRef = doc(db, `artifacts/${__app_id}/users/${userId}/reminders`, reminderId);
    try {
      await updateDoc(reminderRef, { isCompleted: !currentStatus });
      setError('');
    } catch (e) {
      console.error("Error toggling reminder status:", e);
      setError("Failed to update reminder status.");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!userId) {
      setError('User not authenticated.');
      return;
    }
    const confirmed = window.confirm("Are you sure you want to delete this reminder?");
    if (!confirmed) return;

    try {
      const reminderDocRef = doc(db, `artifacts/${__app_id}/users/${userId}/reminders`, reminderId);
      await deleteDoc(reminderDocRef);
      setError('');
    } catch (e) {
      console.error("Error deleting reminder:", e);
      setError("Failed to delete reminder. Please try again.");
    }
  };

  return (
      <div className="bg-white p-8 rounded-lg shadow-xl">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6">Your Reminders</h2>

        <button
            onClick={() => setIsAddingReminder(!isAddingReminder)}
            className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-lg transform hover:scale-105 mb-6"
        >
          {isAddingReminder ? 'Cancel Add Reminder' : 'Add New Reminder'}
        </button>

        {isAddingReminder && (
            <div className="mt-8 p-6 bg-gray-50 rounded-lg shadow-inner mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Add New Reminder</h3>
              <form onSubmit={handleAddReminder} className="space-y-4">
                <div>
                  <label htmlFor="reminderTitle" className="block text-gray-700 text-sm font-semibold mb-2">
                    Title
                  </label>
                  <input
                      type="text"
                      id="reminderTitle"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newReminderTitle}
                      onChange={(e) => setNewReminderTitle(e.target.value)}
                      placeholder="e.g., Pay Electricity Bill"
                      required
                  />
                </div>
                <div>
                  <label htmlFor="reminderDescription" className="block text-gray-700 text-sm font-semibold mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                      id="reminderDescription"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newReminderDescription}
                      onChange={(e) => setNewReminderDescription(e.target.value)}
                      placeholder="e.g., Due on the 15th of every month."
                      rows="2"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="reminderDueDate" className="block text-gray-700 text-sm font-semibold mb-2">
                    Due Date
                  </label>
                  <input
                      type="date"
                      id="reminderDueDate"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newReminderDueDate}
                      onChange={(e) => setNewReminderDueDate(e.target.value)}
                      required
                  />
                </div>
                <button
                    type="submit"
                    className="w-full py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition duration-300 ease-in-out shadow-sm"
                >
                  Create Reminder
                </button>
              </form>
            </div>
        )}

        {reminders.length === 0 ? (
            <p className="text-gray-600">No reminders added yet. Add some manual reminders or credit cards with due dates!</p>
        ) : (
            <div className="space-y-4">
              {reminders.map((reminder) => (
                  <div
                      key={reminder.id}
                      className={`p-5 rounded-lg shadow-md border ${
                          reminder.isCompleted
                              ? 'bg-gray-100 border-gray-200 opacity-70'
                              : new Date(reminder.dueDate) < new Date(new Date().setHours(0,0,0,0))
                                  ? 'bg-red-100 border-red-200' // Overdue
                                  : 'bg-white border-gray-200'
                      }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`text-xl font-bold ${reminder.isCompleted ? 'text-gray-500 line-through' : 'text-indigo-700'}`}>
                          {reminder.title}
                        </h4>
                        {(reminder.type === 'card_payment' || reminder.type === 'card_cut_date' || reminder.type === 'card_payment_reminder') && (
                            <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded-full mt-1 inline-block">
                      {reminder.type === 'card_payment' 
                        ? 'Card Payment' 
                        : reminder.type === 'card_payment_reminder'
                          ? 'Payment Reminder'
                          : 'Cut-off Date'}
                    </span>
                        )}
                        {reminder.description && (
                            <p className="text-gray-600 text-sm mt-1">{reminder.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2 items-center">
                        <button
                            onClick={() => handleToggleComplete(reminder.id, reminder.isCompleted)}
                            className={`px-3 py-1 text-sm rounded-md ${
                                reminder.isCompleted ? 'bg-yellow-500 text-white' : 'bg-green-500 text-white'
                            } hover:opacity-80 transition duration-300 ease-in-out`}
                        >
                          {reminder.isCompleted ? 'Unmark' : 'Complete'}
                        </button>
                        <button
                            onClick={() => handleDeleteReminder(reminder.id)}
                            className="px-3 py-1 bg-red-400 text-white rounded-md text-sm hover:bg-red-500 transition duration-300 ease-in-out"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className={`text-sm font-medium mt-2 ${reminder.isCompleted ? 'text-gray-500' : 'text-gray-700'}`}>
                      Due: {new Date(reminder.dueDate).toLocaleDateString()}
                      {new Date(reminder.dueDate) < new Date(new Date().setHours(0,0,0,0)) && !reminder.isCompleted && (
                          <span className="ml-2 text-red-600 font-bold"> (OVERDUE)</span>
                      )}
                    </p>
                  </div>
              ))}
            </div>
        )}
      </div>
  );
}
